import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Free cash flow (FCF) is the cash a business generates from operations minus the cash it spends to maintain and grow itself (capital expenditures). Unlike reported earnings, FCF is hard to manipulate with accounting choices. It's the money management can actually use to pay dividends, buy back shares, repay debt, or reinvest. Many serious investors trust FCF more than EPS.";

const FAQS = [
  { q: 'What is the formula for free cash flow?', a: 'The simplest version: FCF = Cash from Operations − Capital Expenditures. Both line items appear on the cash flow statement of any annual report.' },
  { q: 'Is free cash flow the same as profit?', a: 'No. Profit (net income) includes non-cash items like depreciation and excludes capital spending. FCF strips both back out and shows real, spendable cash.' },
  { q: 'Can free cash flow be negative?', a: 'Yes. Heavy investment phases (new factories, big acquisitions) can push FCF negative even for healthy companies. Persistent negative FCF is a warning sign.' },
  { q: 'What is FCF yield?', a: 'FCF yield = FCF ÷ market cap. A 5% FCF yield means the company generates 5 cents of free cash for every dollar of market value. It\'s a useful valuation check alongside the P/E ratio.' },
  { q: 'Why do investors prefer FCF to earnings?', a: 'FCF is harder to manipulate. Earnings can be inflated with depreciation tricks, asset sales, or "one-off" items. Cash either landed in the bank or it didn\'t.' },
];

export default function WhatIsFreeCashFlowPage() {
  return (
    <BlogArticleLayout
      slug="what-is-free-cash-flow"
      title="What Is Free Cash Flow and Why Does It Matter?"
      description="Free cash flow is the money a business actually generates after running and growing itself. Learn the formula, sample calculation, and why investors love it."
      keywords="free cash flow, FCF, what is free cash flow, free cash flow formula, FCF yield, cash flow vs earnings, how to calculate FCF"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['revenue-vs-earnings', 'gross-vs-operating-margin', 'how-to-value-a-stock']}
    >
      <section>
        <h2>What is free cash flow?</h2>
        <p>
          Free cash flow is the cash a business produces after paying for the everyday costs of running itself <em>and</em> the longer-term investments it makes in property, equipment, and software (capital expenditures).
          It's the money management can actually return to shareholders, use to buy other companies, or stash on the balance sheet.
        </p>
        <p>
          Formula: <strong>FCF = Cash from Operations − Capital Expenditures</strong>. Both numbers come straight from the cash flow statement.
        </p>
      </section>

      <section>
        <h2>How do you calculate free cash flow?</h2>
        <p>
          Walked example using Apple's last fiscal year:
        </p>
        <ul>
          <li><strong>Cash from operations</strong>: ~$110B</li>
          <li><strong>Capital expenditures</strong>: ~$10B</li>
          <li><strong>Free cash flow</strong>: 110 − 10 = <strong>~$100B</strong></li>
        </ul>
        <p>
          That $100B is what funds Apple's enormous dividend program, share buybacks, and acquisitions. It's the most powerful number on the entire annual report.
        </p>
      </section>

      <section>
        <h2>Why is FCF more honest than earnings?</h2>
        <p>
          The income statement uses accrual accounting: it records sales when they're invoiced, not when the cash arrives, and it spreads asset costs over many years via depreciation. That gives management a lot of room to flatter the numbers.
        </p>
        <p>
          The cash flow statement records what actually moved in and out of the bank. There is much less room to fudge. Two well-known cases:
        </p>
        <ul>
          <li><strong>Enron</strong> reported strong earnings for years while burning cash. The collapse showed up in cash flow first.</li>
          <li><strong>WeWork</strong> had healthy "community-adjusted EBITDA" but persistent negative FCF. The IPO failed once investors looked at cash.</li>
        </ul>
        <p>
          See our companion guide on <Link to="/blog/revenue-vs-earnings">revenue vs earnings</Link> for more on why the bottom line can mislead.
        </p>
      </section>

      <section>
        <h2>What's a "good" free cash flow?</h2>
        <p>
          Two ways to judge it:
        </p>
        <ul>
          <li><strong>FCF margin</strong> = FCF ÷ Revenue. Above 15% is excellent. Software companies often hit 25–35%. Retailers struggle to get above 5%.</li>
          <li><strong>FCF yield</strong> = FCF ÷ Market Cap. Above 5% is solid. Below 2% means you're paying a premium for future growth.</li>
        </ul>
        <p>
          Combine FCF yield with the <Link to="/blog/what-is-pe-ratio">P/E ratio</Link> as a cross-check on valuation.
        </p>
      </section>

      <section>
        <h2>How do investors use free cash flow in practice?</h2>
        <p>
          Three habits worth copying:
        </p>
        <ul>
          <li><strong>Compare FCF to net income.</strong> If they consistently track each other, earnings are real. If FCF is much lower year after year, dig deeper.</li>
          <li><strong>Watch the trend.</strong> FCF growing faster than revenue means the business is becoming more efficient.</li>
          <li><strong>Look at capital intensity.</strong> Companies that need huge capex to grow have a hidden tax on every dollar of revenue. Asset-light businesses tend to compound faster.</li>
        </ul>
        <p>
          For a step-by-step framework that uses FCF alongside other ratios, see our guide on <Link to="/blog/how-to-value-a-stock">how to value a stock</Link>.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
